/**
 * Assinatura digitalizada do profissional, na ficha dele.
 *
 * A imagem sai impressa acima da linha de assinatura nos documentos que ele assina — o bloco de
 * assinaturas dos laudos (PGR, PCMSO, LTCAT…) e o ASO e o prontuário do atendimento concluído. Dá
 * para enviar a assinatura escaneada ou desenhar na hora. É uma imagem: não substitui a assinatura
 * eletrônica, que prova que a pessoa viu aquele documento.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Draw as DrawIcon,
  Gesture as AssinaturaIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { SectionCard } from '../../../components';
import { ActionButton } from '../../../components/common';
import { QuadroDeAssinatura } from '../../../components/assinatura/QuadroDeAssinatura';
import assinaturaDoProfissionalService from '../../../services/assinaturaDoProfissionalService';

const TAMANHO_MAXIMO = 1024 * 1024;

function lerComoDataUrl(arquivo: File): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result));
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

export interface ProfessionalSignatureSectionProps {
  professionalId: number | string;
}

export const ProfessionalSignatureSection: React.FC<ProfessionalSignatureSectionProps> = ({ professionalId }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const entradaDeArquivo = useRef<HTMLInputElement | null>(null);

  const [url, setUrl] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [desenhoAberto, setDesenhoAberto] = useState(false);
  const [desenho, setDesenho] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const nova = await assinaturaDoProfissionalService.verComoUrl(professionalId);
      setUrl((anterior) => {
        if (anterior) URL.revokeObjectURL(anterior);
        return nova;
      });
      setErro(null);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar a assinatura.');
    } finally {
      setCarregando(false);
    }
  }, [professionalId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Solta a URL de objeto quando a seção sai da tela.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );

  const salvar = useCallback(
    async (imagem: string, mensagem: string) => {
      setSalvando(true);
      try {
        await assinaturaDoProfissionalService.salvar(professionalId, imagem);
        enqueueSnackbar(mensagem, { variant: 'success' });
        await carregar();
      } catch (err: unknown) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Não foi possível salvar a assinatura.', { variant: 'error' });
      } finally {
        setSalvando(false);
      }
    },
    [professionalId, carregar, enqueueSnackbar]
  );

  const aoEscolherArquivo = async (arquivo: File | undefined): Promise<void> => {
    if (!arquivo) return;
    if (!['image/png', 'image/jpeg'].includes(arquivo.type)) {
      enqueueSnackbar('Envie a assinatura em PNG ou JPEG.', { variant: 'warning' });
      return;
    }
    if (arquivo.size > TAMANHO_MAXIMO) {
      enqueueSnackbar('A imagem passa de 1 MB. Recorte ou reduza antes de enviar.', { variant: 'warning' });
      return;
    }
    await salvar(await lerComoDataUrl(arquivo), 'Assinatura enviada.');
  };

  const remover = async (): Promise<void> => {
    setSalvando(true);
    try {
      await assinaturaDoProfissionalService.remover(professionalId);
      enqueueSnackbar('Assinatura removida. Os próximos documentos saem com a linha em branco.', { variant: 'info' });
      await carregar();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Não foi possível remover a assinatura.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <SectionCard title="Assinatura digitalizada" icon={<AssinaturaIcon />} accentColor={theme.palette.success.main}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sai impressa acima da linha de assinatura nos laudos, no ASO e no prontuário deste profissional. Sem ela, os
        documentos saem com a linha em branco, para assinar à mão.
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Box
        sx={{
          height: 110,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          // Branco sempre: é como a assinatura sai no papel, inclusive no modo escuro.
          bgcolor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        {carregando ? (
          <CircularProgress size={24} />
        ) : url ? (
          <Box component="img" src={url} alt="Assinatura digitalizada" sx={{ maxHeight: 90, maxWidth: '90%' }} />
        ) : (
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            Nenhuma assinatura cadastrada
          </Typography>
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
        <ActionButton
          acao="professional.update"
          rotulo="Enviar imagem"
          variant="outlined"
          size="small"
          startIcon={<UploadIcon />}
          disabled={salvando}
          onClick={() => entradaDeArquivo.current?.click()}
        >
          Enviar imagem
        </ActionButton>
        <ActionButton
          acao="professional.update"
          rotulo="Desenhar"
          variant="outlined"
          size="small"
          startIcon={<DrawIcon />}
          disabled={salvando}
          onClick={() => {
            setDesenho(null);
            setDesenhoAberto(true);
          }}
        >
          Desenhar
        </ActionButton>
        {url && (
          <ActionButton
            acao="professional.update"
            rotulo="Remover"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            disabled={salvando}
            onClick={() => void remover()}
          >
            Remover
          </ActionButton>
        )}
      </Stack>
      <input
        ref={entradaDeArquivo}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(e) => {
          void aoEscolherArquivo(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        PNG ou JPEG até 1 MB. Prefira a assinatura em tinta escura sobre fundo branco, recortada rente.
      </Typography>

      <Dialog open={desenhoAberto} onClose={() => setDesenhoAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Desenhar a assinatura</DialogTitle>
        <DialogContent>
          <QuadroDeAssinatura onChange={setDesenho} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesenhoAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!desenho || salvando}
            onClick={() => {
              if (!desenho) return;
              setDesenhoAberto(false);
              void salvar(desenho, 'Assinatura salva.');
            }}
          >
            Salvar assinatura
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default ProfessionalSignatureSection;
